import axios from "axios";
import queryString from "query-string";
import { ApiTable } from "./ApiTable";

/**
 * RequestValues — a GET-capable sibling of PostValues (./api-client.js).
 *
 * PostValues hardcodes axios.post and ignores callType, and api-client.js is
 * do-not-touch per pattern.md, so this is a sibling rather than an edit.
 * Its own axios instance means the global 401/403 logout does not apply —
 * a background call here must never sign the user out.
 */

const REQUEST_TIMEOUT_MS = 30000;

/** Encodes "@" as %40 in path params. One line to switch if needed. */
const ENCODE_PATH_PARAMS = true;

const client = axios.create({
  timeout: REQUEST_TIMEOUT_MS,
  // Set here, not inherited — src/axios/index.js mutates the global defaults.
  headers: { "Content-Type": "application/json" },
  transformRequest: [
    (data, headers) => {
      // headers is an AxiosHeaders in axios v1; .get() is the safe accessor.
      const contentType =
        headers && typeof headers.get === "function"
          ? headers.get("Content-Type")
          : headers && headers["Content-Type"];
      return String(contentType || "").includes("application/json")
        ? JSON.stringify(data)
        : data;
    },
  ],
});

// Read per request so a refreshed token is picked up.
client.interceptors.request.use((req) => {
  const token = localStorage.getItem("token");
  if (token) req.headers["X-FS-Authorization"] = token;
  return req;
});

/** True for an aborted request. Callers should also check signal.aborted. */
export function isAbortError(error) {
  return Boolean(
    error &&
      (error.isAbort === true ||
        error.code === "ERR_CANCELED" ||
        error.name === "CanceledError" ||
        error.name === "AbortError")
  );
}

/**
 * Builds a URL, filling ":name" tokens from pathParams.
 * PostValues can only append segments; this API needs email in the middle.
 */
export function buildUrl(apiConfig, { pathParams = {}, queryParams } = {}) {
  if (!apiConfig.baseUrl) {
    // Without this the URL reads "undefined/..." and looks like a CORS bug.
    throw new Error(
      "[request-client] Missing baseUrl for this endpoint. Check the entry in " +
        "utils/ApiTable.js. If it reads from a REACT_APP_* variable, confirm the " +
        ".env file for the environment you are running defines it, then restart " +
        "the dev server (CRA inlines env vars at build time)."
    );
  }

  // A stray trailing slash in .env would give "//" in the path.
  const base = String(apiConfig.baseUrl).replace(/\/+$/, "");

  const path = String(apiConfig.endpoint || "").replace(
    /:([A-Za-z0-9_]+)/g,
    (_match, key) => {
      const value = pathParams[key];
      if (value === undefined || value === null || value === "") {
        throw new Error(
          `[request-client] Missing path param ":${key}" for endpoint "${apiConfig.endpoint}".`
        );
      }
      return ENCODE_PATH_PARAMS
        ? encodeURIComponent(String(value))
        : String(value);
    }
  );

  // A leftover token means a typo; a literal "/:analysisId" would 404.
  const leftover = path.match(/:([A-Za-z0-9_]+)/);
  if (leftover) {
    throw new Error(
      `[request-client] Unsubstituted path param ":${leftover[1]}" in "${apiConfig.endpoint}".`
    );
  }

  const qs =
    queryParams && Object.keys(queryParams).length
      ? `?${queryString.stringify(queryParams)}`
      : "";

  return `${base}${path}${qs}`;
}

/** Normalises a failure into an Error that still carries `status`. */
function toRequestError(error, url) {
  const status = error?.response?.status;
  const serverMessage =
    error?.response?.data?.message || error?.response?.data?.error;

  const normalised = new Error(
    serverMessage || error?.message || "Something went wrong. Please try again."
  );
  normalised.status = status;
  normalised.code = error?.code;
  normalised.data = error?.response?.data;
  normalised.isAbort = isAbortError(error);
  normalised.url = url;
  return normalised;
}

/**
 * Runs an ApiTable entry, honouring its callType.
 *
 * @param {string} name  Key in ApiTable
 * @param {object} options
 * @param {object} [options.body]        Request body (POST/PUT only)
 * @param {object} [options.pathParams]  Values for ":token" segments
 * @param {object} [options.queryParams] Query string values
 * @param {AbortSignal} [options.signal] For cancelling in-flight polls
 * @returns {Promise<any>} `response.data`, matching PostValues' contract
 */
export async function RequestValues(name, options = {}) {
  const apiConfig = ApiTable[name];
  if (!apiConfig) {
    throw new Error(`[request-client] Unknown ApiTable entry "${name}".`);
  }

  const { body, pathParams, queryParams, signal } = options;
  const method = String(apiConfig.callType || "GET").toUpperCase();
  const url = buildUrl(apiConfig, { pathParams, queryParams });

  const requestConfig = { method, url, signal };

  if (method === "GET" || method === "DELETE") {
    // A JSON Content-Type on a bodyless GET forces a needless preflight.
    requestConfig.headers = { "Content-Type": null };
  } else {
    requestConfig.data = body || {};
  }

  try {
    const { data } = await client.request(requestConfig);
    return data;
  } catch (error) {
    throw toRequestError(error, url);
  }
}

export default RequestValues;
